import { t } from "i18next";
import moment from "moment";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { EndPoints } from "../../../../../Constants/endpoints";
import { useToast } from "../../../../../Services/ToastContext";
import {
  FileUpload,
  GeneralButton,
  GeneralInput,
  Modal,
} from "../../../../../components";
import { useAppDispatch, usePostData } from "../../../../../hooks";
import { IStepFields } from "../../../../../interface/MedicalRecord";
import { incrementFileCount } from "../../../../../redux/reducers/investigationFilesSlice";

export interface InvestigationProps {
  Name: string;
  File: File;
}
interface InvestigationInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  fieldConfig: IStepFields;
  name: string;
  disabled: boolean;
}

const InvestigationInputModal = ({
  isOpen,
  onClose,
  fieldConfig,
  name,
  disabled,
}: InvestigationInputModalProps) => {
  const [investigationName, setInvestigationName] = useState<string>();
  const [file, setFile] = useState<File | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const uploadedFiles = watch(name) ?? [];

  const optionsName = `${name}-option`;

  const { showToast } = useToast();
  const dispatch = useAppDispatch();

  const { mutate, isPending } = usePostData(EndPoints.AddMedicalRecordFile);

  return (
    <Modal
      header={<p className="!text-lg">{t("add") + " " + fieldConfig?.Name}</p>}
      isOpen={isOpen}
      onClose={onClose}
    >
      <FileUpload
        name={`${fieldConfig?.Id}.${uploadedFiles?.length}.File`}
        file={file}
        disabled={disabled}
        onFileChange={(selectedFile) => {
          setFile(selectedFile);
          setUploadSuccess(true);
        }}
        onCancel={() => {
          setFile(null);
          setUploadSuccess(false);
        }}
        uploadSuccess={uploadSuccess}
        errorMessageKey={errors.File?.message ? "required_field" : undefined}
        label={fieldConfig?.Name}
        className={"!mb-6"}
      />

      <div className="mt--2 flex flex-col gap-2">
        <div>{t("file_name")}</div>

        <GeneralInput
          name={"fileName"}
          customStyleClasses="rounded-lg h-[50px] min-w-60 px-4 py-2 text-primary-text placeholder-text-muted focus:border-2
                 focus:border-primary focus:bg-white focus:outline-none border border-body-text"
          placeholder={t("enter_file_name")}
          onChange={(event: any) => {
            setInvestigationName(event?.target.value);
          }}
          maxLength={50}
        />
      </div>

      <div className="flex justify-end gap-2">
        <GeneralButton
          className="!w-32"
          color="lite"
          title={t("cancel")}
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
        />
        <GeneralButton
          className="!w-40"
          disabled={!uploadSuccess}
          title={t("save")}
          isLoading={isPending}
          onClick={() => {
            const formData = new FormData();
            formData.append(
              "Name",
              investigationName ?? fieldConfig?.Name + moment().locale("en").format("DD-MM-YYYY hh:mm:ss"),
            );
            formData.append("File", file as any);
            formData.append("Date", moment().locale("en").format("DD-MM-YYYY"));
            mutate(formData, {
              onSuccess: (data: any) => {
                setValue("fileName",undefined);
                dispatch(incrementFileCount(name));
                setValue(name, [...uploadedFiles, `${data?.Data?.Id}`]);
                setValue(optionsName, [...(watch(optionsName) ?? []), { File: file, FieldId: fieldConfig?.Id, ...data?.Data }]);
                onClose();
              },
              onError: (error) => {
                showToast(error.message, "error");
              },
            });
          }}
        />
      </div>
    </Modal>
  );
};

export default InvestigationInputModal;
